import { repository } from '../../data'
import type { View } from './derive'
import { useWrite } from './parts'
import { ago } from './format'

const DAY = 86_400_000

/** What needs an admin's eye, worst first. Every line is a fact, not a guess. */
export function needsALook(view: View): readonly string[] {
  const out: string[] = []
  const now = Date.parse(view.now)
  const last = view.page.push.last
  if (last && last.outcome !== 'pushed') out.push(`The last push failed: ${last.error ?? last.outcome}`)
  if (view.page.devicesError) out.push(`Could not list devices: ${view.page.devicesError}`)
  if (view.unclaimed.length) out.push(`${view.unclaimed.length} ${view.unclaimed.length === 1 ? 'device on the network is' : 'devices on the network are'} not managed here yet`)
  if (view.networkOnly.length) out.push(`${view.networkOnly.length} ${view.networkOnly.length === 1 ? 'person on the network has' : 'people on the network have'} no Kronos account`)
  const absent = view.machines.filter((m) => m.net === 'not_joined')
  if (absent.length) out.push(`${absent.length} ${absent.length === 1 ? 'machine has' : 'machines have'} never joined the network`)
  const ending = view.ending.filter((r) => Date.parse(r.expiresAt as string) - now < DAY)
  if (ending.length) out.push(`${ending.length} ${ending.length === 1 ? 'access ends' : 'accesses end'} within a day`)
  const lapsing = view.people.flatMap((p) => p.devices).filter((d) => d.keyExpiry && Date.parse(d.keyExpiry) - now < 14 * DAY)
  if (lapsing.length) out.push(`${lapsing.length} ${lapsing.length === 1 ? 'laptop needs' : 'laptops need'} to sign in again within two weeks`)
  return out
}

export function StatusStrip({ view }: { view: View }): JSX.Element {
  const push = useWrite(() => repository.syncNetwork().then((sync) => ({ sync })), () => 'Checked the network rules')
  const last = view.page.push.last
  const failed = last !== null && last.outcome !== 'pushed'
  const robots = view.machines.filter((m) => m.kind === 'robot')
  const pcs = view.machines.filter((m) => m.kind === 'computer')
  const up = (xs: typeof robots) => xs.filter((m) => m.net === 'online').length
  const withAccess = view.people.filter((p) => p.robots + p.computers > 0)
  const look = needsALook(view)

  return (
    <div className="cx-strip">
      <div className={`cx-tile ${failed ? 'is-bad' : 'is-good'}`}>
        <span className="cx-tile__label">Network rules</span>
        <span className="cx-tile__state">
          <span className={`net-dot ${failed ? 'is-bad' : 'is-online'}`} aria-hidden="true" />
          {failed ? (last.outcome === 'rejected' ? 'Rejected' : 'Unreachable') : 'In sync'}
        </span>
        <span className="cx-tile__sub">
          {view.page.push.lastSuccessAt ? `Pushed ${ago(view.page.push.lastSuccessAt)}` : 'Not pushed yet'}
          <button className="cx-tile__act" disabled={push.pending} onClick={() => push.run()}>
            {push.pending ? 'Pushing…' : 'Push now'}
          </button>
        </span>
      </div>
      <div className="cx-tile">
        <span className="cx-tile__label">Robots online</span>
        <span className="cx-tile__value">
          {up(robots)}
          <span className="cx-tile__of"> / {robots.length}</span>
        </span>
        <span className="cx-tile__sub">
          {robots.filter((m) => m.net === 'offline').length} offline · {robots.filter((m) => m.net === 'not_joined').length} not joined
        </span>
      </div>
      <div className="cx-tile">
        <span className="cx-tile__label">Office computers online</span>
        <span className="cx-tile__value">
          {up(pcs)}
          <span className="cx-tile__of"> / {pcs.length}</span>
        </span>
        <span className="cx-tile__sub">{pcs.length ? 'GPUs and the like' : 'None added yet'}</span>
      </div>
      <div className="cx-tile">
        <span className="cx-tile__label">People online</span>
        <span className="cx-tile__value">
          {withAccess.filter((p) => p.online).length}
          <span className="cx-tile__of"> / {withAccess.length}</span>
        </span>
        <span className="cx-tile__sub">of those who can SSH somewhere</span>
      </div>
      <div className={`cx-tile ${look.length ? 'is-warn' : ''}`}>
        <span className="cx-tile__label">{look.length ? `Needs a look · ${look.length}` : 'Needs a look'}</span>
        <span className="cx-tile__text">{look[0] ?? 'Nothing right now'}</span>
        {look.length > 1 ? <span className="cx-tile__sub">and {look.length - 1} more</span> : null}
      </div>
    </div>
  )
}
